import { AlertTriangle, CheckCircle2, Info, XCircle, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export type InlineAlertTone = "info" | "success" | "warning" | "error";

const TONE: Record<InlineAlertTone, { cls: string; Icon: LucideIcon }> = {
  info: { cls: "border-info-border bg-info-muted text-info-muted-foreground", Icon: Info },
  success: {
    cls: "border-success-border bg-success-muted text-success-muted-foreground",
    Icon: CheckCircle2,
  },
  warning: {
    cls: "border-warning-border bg-warning-muted text-warning-muted-foreground",
    Icon: AlertTriangle,
  },
  error: { cls: "border-destructive/30 bg-destructive/10 text-destructive", Icon: XCircle },
};

/**
 * InlineAlert (CHP-522) — the standard banner for page/section-level feedback
 * (a tier above `FormError`'s single line). One look per tone, on the semantic
 * token lanes, dark-mode safe. Replaces the ~72 bespoke `role="alert"` banners
 * (audit X6/X17).
 */
export function InlineAlert({
  tone = "info",
  title,
  children,
  className,
}: {
  tone?: InlineAlertTone;
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const { cls, Icon } = TONE[tone];
  return (
    <div
      role="alert"
      className={cn("flex items-start gap-2.5 rounded-xl border p-3 text-sm", cls, className)}
    >
      <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <p className={cn(title && "opacity-90")}>{children}</p> : null}
      </div>
    </div>
  );
}
